import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, TrendingDown } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { checkupEinstellungenAPI } from '@/lib/api';

/* Einrichtung „Ergebnis- und Abschluss-Check-up“ (Coach-Cockpit W1.6/W1.6b, backend/checkup_einstellungen.py): Abschwächung je
   Folgemonat und Spanne der „Einschätzung deines Coaches“, Schnellbausteine für den Satz des Coaches, Zuordnung Ziel →
   Kursarten (Kategorie im Kursplan) für den Abschluss-Check-up. Lesen mit leads.edit, ändern nur die Leitung. */

const ZIEL_TEXT = { kraft: 'Kraft, Muskelaufbau', fettverlust: 'Fettverlust, Kondition', frauen: 'Frauen, Kraft und Wohlbefinden', kampfsport: 'Kampfsport' };
const fehler = (e) => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : 'Speichern fehlgeschlagen');

export default function ErgebnisEinrichtungKarte() {
  const [d, setD] = useState(null);
  const [form, setForm] = useState(null);
  const [arbeitet, setArbeitet] = useState(false);
  const uebernehmen = (r) => {
    setD(r);
    const e = r.einschaetzung || {};
    setForm({
      abschwaechung: String(e.abschwaechung ?? 0.8).replace('.', ','), spanne: String(e.spanne_pct ?? 25).replace('.', ','),
      bausteine: (e.bausteine || []).join('\n'),
      zuordnung: Object.fromEntries((r.kurs_zuordnung || []).map((z) => [z.ziel, (z.kursarten || []).join(', ')])),
    });
  };
  const lade = useCallback(async () => {
    try { uebernehmen((await checkupEinstellungenAPI.lesen()).data); } catch { setD({ fehler: true }); }
  }, []);
  useEffect(() => { lade(); }, [lade]);
  if (!d) return null;
  if (d.fehler) return null;                                  // ohne Recht keine Karte
  const darf = !!d.darf_bearbeiten;
  const speichern = async () => {
    setArbeitet(true);
    try {
      const zahl = (v) => Number(String(v).replace(',', '.'));
      const r = await checkupEinstellungenAPI.setzen({
        einschaetzung: { abschwaechung: zahl(form.abschwaechung), spanne_pct: zahl(form.spanne),
          bausteine: form.bausteine.split('\n').map((t) => t.trim()).filter(Boolean) },
        kurs_zuordnung: (d.auswahl?.kurs_ziele || Object.keys(ZIEL_TEXT)).map((ziel) => ({
          ziel, kursarten: String(form.zuordnung[ziel] || '').split(',').map((k) => k.trim()).filter(Boolean) })),
      });
      uebernehmen({ ...r.data, darf_bearbeiten: true });
      toast.success('Gespeichert');
    } catch (e) { toast.error(fehler(e)); } finally { setArbeitet(false); }
  };
  const feld = 'h-11 rounded-md border border-border bg-background px-2 text-sm w-full';
  return (
    <Card className="border-border/60" data-testid="ergebnis-einrichtung-karte">
      <CardContent className="p-4 space-y-3">
        <h3 className="text-sm font-semibold flex items-center gap-2"><TrendingDown className="w-4 h-4 text-marke" /> Ergebnis- und Abschluss-Check-up</h3>
        <p className="text-xs text-muted-foreground">Einschätzung des Coaches: der Verlauf der ersten 4 Wochen, je Folgemonat abgeschwächt, als Spanne.
          Der Trainer bestätigt immer, bevor der Kunde etwas sieht. Ändern darf nur die Leitung.</p>
        <div className="grid grid-cols-2 gap-2 text-xs">
          <label>Abschwächung je Folgemonat (0–1)<input className={feld} disabled={!darf} value={form.abschwaechung}
            onChange={(e) => setForm({ ...form, abschwaechung: e.target.value })} inputMode="decimal" /></label>
          <label>Spanne ± (%)<input className={feld} disabled={!darf} value={form.spanne} onChange={(e) => setForm({ ...form, spanne: e.target.value })}
            inputMode="decimal" /></label>
          <label className="col-span-2">Schnellbausteine für den Satz des Coaches (eine Zeile je Satz)
            <textarea className="w-full rounded-md border border-border bg-background p-2 text-sm min-h-[88px]" disabled={!darf} value={form.bausteine}
              onChange={(e) => setForm({ ...form, bausteine: e.target.value })} /></label>
        </div>
        <div className="space-y-1">
          <p className="text-xs font-semibold">Abschluss-Check-up: Ziel → Kursarten (Kategorie im Kursplan, mit Komma)</p>
          {(d.auswahl?.kurs_ziele || Object.keys(ZIEL_TEXT)).map((ziel) => (
            <label key={ziel} className="grid grid-cols-[10rem_1fr] items-center gap-2 text-xs">
              <span>{ZIEL_TEXT[ziel] || ziel}</span>
              <input className={feld} disabled={!darf} value={form.zuordnung[ziel] || ''} placeholder="z. B. sc, hiit"
                onChange={(e) => setForm({ ...form, zuordnung: { ...form.zuordnung, [ziel]: e.target.value } })} />
            </label>
          ))}
          <p className="text-[11px] text-muted-foreground">Empfohlen werden nur Kurse mit freien Plätzen in der kommenden Woche.</p>
        </div>
        {darf && <Button size="sm" className="h-11" disabled={arbeitet} onClick={speichern}>{arbeitet ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Speichern'}</Button>}
      </CardContent>
    </Card>
  );
}
